import { useContext, createContext, PropsWithChildren } from 'react';

import type {
  TPolicy,
  IAutoQuote,
  IRentersQuote,
  ERegionCode,
} from '@sureapp/platform-sdk';

import {
  getRegionCode,
  emptyRegionCode,
  EOptionalRegionCode,
} from '@@/common/services/region';

type TRegionContextValue = {
  regionCode: EOptionalRegionCode;
};

export const RegionContext = createContext<TRegionContextValue>({
  regionCode: emptyRegionCode,
});

export function useRegion<hasRegion extends boolean>() {
  const context = useContext(RegionContext);

  // When this hook is invoked as `useRegion()` (without TS generic types),
  // EOptionalRegionCode is returned instead of `ERegionCode. This is
  // technically safer because it's not always guaranteed that a region code
  // will exist. However, in some places it's clear a region code will be
  // available (e.g., in self-service when viewing a policy). In these cases
  // it's preferable to invoke this hook `useRegion<true>(). This simplifies
  // downstream code since we don't have to handle the empty case.
  return context as {
    regionCode: hasRegion extends true ? ERegionCode : EOptionalRegionCode;
  };
}

type TQuote = IAutoQuote | IRentersQuote;
type TRegionProvider = PropsWithChildren<
  { quote: TQuote; policy?: never } | { policy: TPolicy; quote?: never }
>;

export function RegionProvider({ children, quote, policy }: TRegionProvider) {
  const regionCode = getRegionCode(quote || policy);

  return (
    <RegionContext.Provider value={{ regionCode }}>
      {children}
    </RegionContext.Provider>
  );
}
